/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star, CheckCircle2 } from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Textarea } from "./components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Rating = ({ value, onChange, max = 5, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {Array.from({ length: max === 5 ? 5 : 11 }, (_, i) => (max === 5 ? i + 1 : i)).map((n) => (
      <button
        key={n} type="button" onClick={() => onChange(n)}
        className={`h-10 min-w-10 px-2 rounded-lg border-2 font-bold text-sm transition-all ${value === n ? "bg-[#F07B1D] border-[#F07B1D] text-white scale-105" : "bg-white border-gray-200 text-gray-600 hover:border-[#F07B1D]"}`}
        data-testid={`${testId}-${n}`}
      >
        {max === 5 ? <Star className={`h-4 w-4 ${value >= n ? "fill-current" : ""}`} /> : n}
      </button>
    ))}
  </div>
);

export default function EnquetePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/enquete/${token}`).then((r) => { setData(r.data); setDone(r.data.answered); }).catch((e) => setError(e.response?.data?.detail || "Lien invalide"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    const missing = data.questions.find((q) => q.type !== "text" && answers[q.id] === undefined);
    if (missing) return toast.error(`Merci de répondre : « ${missing.label} »`);
    setSaving(true);
    try {
      await axios.post(`${API}/enquete/${token}`, { answers, consent_temoignage: consent });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#F8FAFC]" data-testid="enquete-page">
      <div className="container-custom max-w-2xl">
        <Card className="bg-white p-2">
          <CardContent className="p-6 md:p-8">
            {error && <p className="text-red-600 font-semibold" data-testid="enquete-error">{error}</p>}
            {data && done && (
              <div className="text-center py-8" data-testid="enquete-done">
                <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto mb-4" />
                <h1 className="font-heading text-2xl font-bold text-[#003087] mb-2">Merci {data.prenom} !</h1>
                <p className="text-gray-600">Votre retour a bien été enregistré et alimente notre démarche d'amélioration continue (Qualiopi).</p>
              </div>
            )}
            {data && !done && (
              <form onSubmit={submit} className="space-y-7">
                <div>
                  <p className="text-[#F07B1D] font-semibold text-xs uppercase tracking-wider mb-1">{data.kind === "chaud" ? "Questionnaire de satisfaction à chaud" : "Questionnaire à froid (J+30)"}</p>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#003087] leading-tight">Votre avis sur « {data.formation_title} »</h1>
                  <p className="text-gray-600 text-sm mt-2">Bonjour {data.prenom}, 2 minutes suffisent. Vos réponses sont confidentielles et utilisées pour améliorer nos formations.</p>
                </div>
                {data.questions.map((q) => (
                  <div key={q.id} data-testid={`question-${q.id}`}>
                    <label className="block font-semibold text-[#003087] text-sm mb-2">{q.label}</label>
                    {q.type === "rating" && <Rating value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} testId={`rating-${q.id}`} />}
                    {q.type === "nps" && <Rating value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} max={10} testId={`nps-${q.id}`} />}
                    {q.type === "text" && <Textarea rows={3} value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} data-testid={`text-${q.id}`} />}
                  </div>
                ))}
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="consent-temoignage" />
                  J'autorise MGB School à publier mon commentaire comme témoignage (prénom et formation uniquement).
                </label>
                <Button type="submit" disabled={saving} className="w-full bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold py-6" data-testid="enquete-submit">
                  {saving ? "Envoi…" : "Envoyer mes réponses"}
                </Button>
                <p className="text-xs text-gray-500">Données traitées par MGB School (responsable de traitement) à des fins de suivi qualité — art. 6.1.f RGPD. Droits d'accès et de suppression : contact@mgbschool.fr.</p>
              </form>
            )}
            {!data && !error && <p className="text-gray-500">Chargement…</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
